import { Calendar, ExternalLink, AlertCircle, RefreshCw } from "lucide-react";
import { useEffect, useState } from "react";

const DataFetcherCongresos = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const modalidadMap = {
    en_linea: "En línea",
    presencial: "Presencial",
    hibrida: "Híbrida",
  };

  const getModalidadBadge = (modalidad) => {
    const mod = modalidad?.toLowerCase() || "";
    if (mod.includes("línea") || mod.includes("linea")) {
      return "bg-emerald-50 text-emerald-700 border-emerald-200/80";
    }
    if (mod.includes("híbrida") || mod.includes("hibrida")) {
      return "bg-purple-50 text-purple-700 border-purple-200/80";
    }
    return "bg-blue-50 text-blue-700 border-blue-200/80";
  };

  const formatFecha = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return isoString;

    return new Intl.DateTimeFormat("es-EC", {
      day: "numeric",
      month: "short",
      year: "numeric",
    }).format(date);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const url =
          "https://eventos.utpl.edu.ec/jsonapi/node/congresos?" +
          "fields[node--congresos]=title,field_nombre_de_evento,field_modalidad,field_fecha,field_fecha_fin,field_enlace";

        const response = await fetch(url);
        if (!response.ok) throw new Error("No se pudo obtener la información.");
        const json = await response.json();

        const items = json.data || [];

        // Obtenemos el inicio del día de hoy (00:00:00) para comparar correctamente
        const hoy = new Date();
        hoy.setHours(0, 0, 0, 0);

        // 1. Transformar y mapear los campos
        const transformed = items
          .map((item) => {
            const attrs = item.attributes || {};

            let modalidadFormateada = attrs.field_modalidad;
            if (Array.isArray(attrs.field_modalidad)) {
              modalidadFormateada = attrs.field_modalidad
                .map((m) => modalidadMap[m] || m)
                .join(", ");
            } else if (typeof attrs.field_modalidad === "string") {
              modalidadFormateada =
                modalidadMap[attrs.field_modalidad] || attrs.field_modalidad;
            }

            const rawEnlace = attrs.field_enlace;
            const urlEnlace =
              typeof rawEnlace === "object" ? rawEnlace?.uri : rawEnlace;

            const fechaInicio = formatFecha(attrs.field_fecha);
            const fechaFin = formatFecha(attrs.field_fecha_fin);

            return {
              id: item.id,
              rawFecha: attrs.field_fecha || "",
              rawFechaFin: attrs.field_fecha_fin || attrs.field_fecha || "",
              field_nombre_de_evento:
                attrs.field_nombre_de_evento ||
                attrs.title ||
                "Evento sin título",
              field_modalidad: modalidadFormateada || "No especificada",
              field_fecha: fechaInicio,
              field_fecha_fin: fechaFin,
              mismadecha: fechaInicio === fechaFin,
              field_enlace: urlEnlace || "#",
            };
          })
          // 2. Filtrar: mantener solo los eventos cuya fecha fin (o inicio) sea mayor o igual a hoy
          .filter((evento) => {
            if (!evento.rawFechaFin) return true; // Si no tiene fecha, lo mantenemos por precaución
            const fechaFinEvento = new Date(evento.rawFechaFin);
            
            // Si la fecha ISO viene solo como 'YYYY-MM-DD', le sumamos ajuste de zona horaria local
            fechaFinEvento.setHours(23, 59, 59, 999);
            
            return fechaFinEvento.getTime() >= hoy.getTime();
          });

        // 3. Ordenar cronológicamente
        transformed.sort((a, b) => {
          const dateA =
            new Date(a.rawFecha).getTime() || Number.MAX_SAFE_INTEGER;
          const dateB =
            new Date(b.rawFecha).getTime() || Number.MAX_SAFE_INTEGER;
          return dateA - dateB;
        });

        setData(transformed);
      } catch (err) {
        console.error(err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 p-6 max-w-7xl mx-auto">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div
            key={i}
            className="h-56 bg-slate-100 rounded-2xl animate-pulse p-6 flex flex-col justify-between border border-slate-200/60"
          >
            <div className="space-y-3">
              <div className="h-5 bg-slate-200 rounded-md w-1/4"></div>
              <div className="h-6 bg-slate-200 rounded-md w-3/4"></div>
              <div className="h-4 bg-slate-200 rounded-md w-1/2"></div>
            </div>
            <div className="h-10 bg-slate-200 rounded-xl w-full mt-4"></div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center p-12">
        <div className="flex items-center gap-3 bg-red-50 text-red-700 border border-red-200 rounded-xl p-4 max-w-md shadow-sm">
          <AlertCircle className="w-5 h-5 shrink-0 text-red-500" />
          <p className="text-sm font-medium">
            Error al cargar los congresos: {error}
          </p>
        </div>
      </div>
    );
  }

  return (
    <section className="p-4 sm:p-0 max-w-6xl mx-auto">
      <div className="py-24">
        <div className="text-center mb-10">
          <h2 className="font-bold  ">
            Conoce nuestros congresos
          </h2>
          <p  >
            Encuentros académicos, científicos y culturales que reunirán a
            expertos, investigadores y profesionales de distintos países.
          </p>
        </div>

        {data.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            No hay congresos próximos registrados por el momento.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {data.map((evento) => (
              <article
                key={evento.id}
                className="group flex flex-col justify-between bg-white border border-slate-200/80 rounded-2xl py-4 px-5 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 ease-in-out"
              >
                <div className="space-y-2">
                  <div>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${getModalidadBadge(
                        evento.field_modalidad
                      )}`}
                    >
                      {evento.field_modalidad}
                    </span>
                  </div>

                  <h3 className="font-bold text-base p-0 m-0 ">
                    {evento.field_nombre_de_evento}
                  </h3>

                  {evento.field_fecha && (
                    <div className="flex items-center gap-2 text-slate-500 text-xs font-medium">
                      <Calendar className="w-4 h-4 shrink-0 text-slate-400" />
                      <span>
                        {evento.mismadecha || !evento.field_fecha_fin
                          ? evento.field_fecha
                          : `${evento.field_fecha} - ${evento.field_fecha_fin}`}
                      </span>
                    </div>
                  )}
                </div>

                {evento.field_enlace !== "#" && (
                  <div className="mt-6 pt-4 border-t border-slate-100">
                    <a
                      href={evento.field_enlace}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-[#004270] hover:bg-[#febe10] hover:text-[#004270] text-white font-medium text-sm rounded-xl transition-all duration-200 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    >
                      <span>Ver evento</span>
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                )}
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default DataFetcherCongresos;